import React, {useEffect} from "react";
import {GlobalDictionary} from "../../Utils/globalDictionary";
import {PROFILEIMAGE} from "../../Utils/ProfilImage/ProfileImageApi";
import {odxIconSets} from "../constants";
import OdxTooltip from "./OdxTooltip";

interface OdxAvatarProps extends React.HTMLAttributes<HTMLElement> {
    size: "small" | "medium" | "large";
    variant: "circle" | "rectangle";
    mail: string;
    className?: string;
    disableTooltip?: boolean; // New prop to disable tooltip
    key?: number;
    forceSmallIcon?: boolean;
    alternativeIcon?: "user" | "folder";
    style?: React.CSSProperties;
}

/**
 * Avatar component that displays a user's profile image
 * @param className additional class names for the odx-avatar
 * @param mail mail address of the user
 * @param size small, medium or large
 * @param variant circle or rectangle
 * @param disableTooltip flag to disable tooltip (default is false)
 * @param rest additional props for the odx-avatar
 * @param key key of the odx-avatar
 * @constructor
 */
const MailAvatar: React.FC<OdxAvatarProps> = ({
                                                  className,
                                                  mail,
                                                  size = "medium",
                                                  variant = "circle",
                                                  disableTooltip = false,
                                                  key,
                                                  forceSmallIcon = false,
                                                  alternativeIcon = "user",
                                                  style,
                                                  ...rest
                                              }) => {
    const [image, setImage] = React.useState<string | undefined>(undefined);

    useEffect(() => {
        if (GlobalDictionary.get(PROFILEIMAGE + mail) !== undefined) {
            getImage(PROFILEIMAGE + mail);
        } else {
            GlobalDictionary.subscribe(() => getImage(PROFILEIMAGE + mail));
        }
        return () => {
            GlobalDictionary.unsubscribe(() => getImage(PROFILEIMAGE + mail));
        };
    }, []);

    useEffect(() => {
        getImage(PROFILEIMAGE + mail);
    }, [mail]);

    const getImage = (key: string) => {
        const image = GlobalDictionary.get(key);
        setImage(image);
        GlobalDictionary.unsubscribe(() => getImage(PROFILEIMAGE + mail));
    };

    const resolvedShape = variant === "rectangle" ? "rectangle" : "circle";
    const resolvedSrc = image ? `data:image/jpeg;base64,${image}` : undefined;

    const sizeOverrides: Record<OdxAvatarProps["size"], string> = {
        small: "48px",
        medium: "64px",
        large: "80px"
    };

    const appliedSize = forceSmallIcon ? "42px" : sizeOverrides[size];

    const hostClasses = [
        "flex items-center justify-center",
        className
    ].filter(Boolean).join(" ");

    const hostStyle: React.CSSProperties = {
        ...(style as React.CSSProperties),
        "--size": appliedSize
    };

    const avatarElement = (
        <odx-avatar
            key={key}
            size="xxl"
            shape={resolvedShape}
            variant="ghost"
            className={hostClasses}
            style={hostStyle}
            {...rest}
        >
            {resolvedSrc ? (
                <img
                    src={resolvedSrc}
                    alt="Profile Image"
                    className="object-cover !w-[70%] !h-[70%]"
                />
            ) : (
                <odx-icon name={`${odxIconSets.core}::${alternativeIcon}`} size="xxl"></odx-icon>
            )}
        </odx-avatar>
    );

    if (disableTooltip) {
        return avatarElement;
    }

    return <OdxTooltip text={mail}>
        {avatarElement}
    </OdxTooltip>;

}

export default MailAvatar;
